const BASE = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "cashback_auth_token";

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

async function request(path, options = {}) {
  const headers = { "Content-Type": "application/json" };
  if (options.auth !== false) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  let res;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method: options.method || "GET",
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  } catch (networkErr) {
    // fetch() itself failed (offline, DNS, SSL, etc.)
    const e = new Error("Network error. Please check your connection.");
    e.status = 0;
    e.networkError = true;
    throw e;
  }

  // Try to read the body, but swallow iOS Safari "body is disturbed or locked"
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }

  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join(", ")
        : detail?.msg || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  register: (email, password, name, referral_code) =>
    request("/auth/register", {
      method: "POST",
      body: { email, password, name, referral_code: referral_code || null },
      auth: false,
    }),
  login: (email, password) =>
    request("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false,
    }),
  forgotPassword: (email) =>
    request("/auth/forgot-password", {
      method: "POST",
      body: { email },
      auth: false,
    }),
  resetPassword: (token, password) =>
    request("/auth/reset-password", {
      method: "POST",
      body: { token, password },
      auth: false,
    }),
  me: () => request("/auth/me"),
  myReferrals: () => request("/referrals/me"),
  claimDemoBonus: (amount) =>
    request("/demo/claim-bonus", { method: "POST", body: { amount } }),

  uploadReceipt: (image_base64, note) =>
    request("/receipts/upload", {
      method: "POST",
      body: { image_base64, note },
    }),
  myReceipts: () => request("/receipts/mine"),
  receipt: (id) => request(`/receipts/${id}`),

  createCashout: (payload) =>
    request("/cashouts", {
      method: "POST",
      body: payload,
    }),
  myCashouts: () => request("/cashouts/mine"),
  walletTransactions: () => request("/wallet/transactions"),

  notifications: () => request("/notifications"),
  markRead: () => request("/notifications/mark-read", { method: "POST" }),
  unreadCount: () => request("/notifications/unread-count"),

  adminReceipts: (status) =>
    request(`/admin/receipts${status ? `?status_filter=${status}` : ""}`),
  adminApprove: (receipt_id, amount, message, points) =>
    request("/admin/receipts/approve", {
      method: "POST",
      body: { receipt_id, amount, message, ...(points != null ? { points } : {}) },
    }),
  adminReject: (receipt_id, message) =>
    request("/admin/receipts/reject", {
      method: "POST",
      body: { receipt_id, message },
    }),
  adminCashouts: (status) =>
    request(`/admin/cashouts${status ? `?status_filter=${status}` : ""}`),
  adminPay: (cashout_id, message) =>
    request("/admin/cashouts/mark-paid", {
      method: "POST",
      body: { cashout_id, message },
    }),
  adminRejectCashout: (cashout_id, message) =>
    request("/admin/cashouts/reject", {
      method: "POST",
      body: { cashout_id, message },
    }),
  adminUsers: () => request("/admin/users"),
  adminAdjustBalance: (user_id, amount, message) =>
    request("/admin/users/adjust-balance", {
      method: "POST",
      body: { user_id, amount, message },
    }),
  adminSendMessage: (user_id, title, body) =>
    request("/admin/notifications/send", {
      method: "POST",
      body: { user_id, title, body },
    }),
  adminBroadcast: (title, body) =>
    request("/admin/notifications/broadcast", {
      method: "POST",
      body: { title, body },
    }),
  adminStripeBalance: () => request("/admin/stripe/balance"),

  promo: () => request("/promo"),
  platformStats: () => request("/stats/platform"),
  adminGetPromo: () => request("/admin/promo"),
  adminSetPromo: (active, text) =>
    request("/admin/promo", { method: "POST", body: { active, text } }),
};
